import { useEffect, useState } from 'react';
import { List } from 'immutable';
import { useDispatch, useSelector } from 'react-redux';

import useLabelHandlers from './useLabelHandlers';

import { Label } from '../../../../../../../common/labels/labelTypes';
import { selectSelectedElementsNormalisedForTaskLists } from '../../../../selector/selectionToolsSelector';
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import { getLabelsAreStale, selectUsableLabels } from '../../../../../../element/labels/labelSelectors';
import { fetchLabelsByIds, fetchLabelsFromDb } from '../../../../../../element/labels/labelsService';
import { reloadLabels } from '../../../../../../../common/labels/labelActions';

type ReturnType = {
    allLabels: Label[];
    elements: List<ImMNElement>;
    searchString: string;
    setSearchString: (value: string) => void;
    handleCreateLabel: () => void;
    handleSearchSubmit: () => void;
    handleToggleLabelSelection: (labelId: string) => void;
};

type Props = {
    currentUserId: string;
    isActive?: boolean;
};

const useLabelsTool = ({ currentUserId, isActive = true }: Props): ReturnType => {
    const dispatch = useDispatch();
    const labelsAreStale = useSelector(getLabelsAreStale);
    const labelMap = useSelector(selectUsableLabels);
    const elements: List<ImMNElement> = useSelector(selectSelectedElementsNormalisedForTaskLists);
    const allLabels = Object.values(labelMap || {});
    const [searchString, setSearchString] = useState('');

    useEffect(() => {
        if (!isActive) return;

        const shouldReloadLabelsOnOpen = labelsAreStale || !allLabels.length;
        if (!shouldReloadLabelsOnOpen) return;

        if (labelsAreStale) {
            fetchLabelsByIds(allLabels.map((label) => label._id)).then((labels) => dispatch(reloadLabels(labels)));
            return;
        }

        fetchLabelsFromDb().then((labels) => dispatch(reloadLabels(labels)));
    }, [allLabels, dispatch, isActive, labelsAreStale]);

    const { handleToggleLabelSelection, handleCreateLabel, handleSearchSubmit } = useLabelHandlers(
        currentUserId,
        searchString,
        setSearchString,
        elements,
        allLabels,
    );

    return {
        allLabels,
        elements,
        searchString,
        setSearchString,
        handleCreateLabel,
        handleSearchSubmit,
        handleToggleLabelSelection,
    };
};

export default useLabelsTool;
